import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { postNamedCommand } from '../../../../../service'
import SydModal from '../../../../commonDesign/SydModal'
import ActivityForm from './ActivityForm'

const CREATE_DEFAULTS = {
  subject: '',
  start_date: '',
  end_date: '',
  duration: 60,
  all_day: false,
  contacts: [],
  location: '',
  description: '',
  priority: 1,
  repeats: 'never',
  category_id: '',
  activity_code_id: undefined,
  importance: undefined
}

function CreateActivityModal ({
  open,
  onClose,
  config,
  members,
  formMode = 'calendar',
  defaultActivityCodeId,
  createLabel = 'Create Activity',
  invalidateQueryKeys,
  categoryOptions = []
}) {
  const { clientId } = useAppContext()
  const queryClient = useQueryClient()
  const [bladeOpen, setBladeOpen] = useState(false)

  const isCalendar = formMode === 'calendar'

  const defaultValues = {
    ...CREATE_DEFAULTS,
    category_id: config?.categoryIds?.[0] ?? '',
    activity_code_id: defaultActivityCodeId ?? undefined
  }

  const handleClose = useCallback(() => {
    setBladeOpen(false)
    onClose()
  }, [onClose])

  const { mutateAsync: handleSubmit } = useMutation({
    mutationFn: (body) => postNamedCommand('coreData', 'execute-redtail-domain-command', {
      command: 'createActivity',
      autoResolveConnection: true,
      clientId,
      request: { body }
    }),
    onSuccess: () => {
      handleClose()
      if (invalidateQueryKeys) {
        for (const key of invalidateQueryKeys) {
          queryClient.invalidateQueries({ queryKey: key })
        }
      }
    }
  })

  const showBlade = isCalendar && bladeOpen

  return (
    <SydModal
      open={open}
      onClose={handleClose}
      title={createLabel}
      size={showBlade ? 'large' : 'xmedium'}
      disablePortal={false}
    >
      <ActivityForm
        defaultValues={defaultValues}
        config={config}
        members={members}
        categoryOptions={categoryOptions}
        onSubmit={handleSubmit}
        onCancel={handleClose}
        submitLabel={createLabel}
        cancelVariant='outline'
        formMode={formMode}
        showTypeField
        bladeOpen={bladeOpen}
        onBladeChange={setBladeOpen}
      />
    </SydModal>
  )
}

CreateActivityModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  config: PropTypes.object,
  members: PropTypes.array,
  formMode: PropTypes.oneOf(['calendar', 'simple']),
  defaultActivityCodeId: PropTypes.number,
  createLabel: PropTypes.string,
  invalidateQueryKeys: PropTypes.arrayOf(PropTypes.array),
  categoryOptions: PropTypes.array
}

export default CreateActivityModal
